import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../context/AuthContext';
import { Plus, Loader2, Archive, ArchiveRestore, X, Users, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useSubjects } from '../hooks/useSubjects';
import { LEVELS } from '../lib/subjects';

const pctColor = (p) => p >= 70 ? 'text-emerald-600 bg-emerald-50' : p >= 40 ? 'text-amber-600 bg-amber-50' : 'text-rose-600 bg-rose-50';

export default function TeacherClassesPage() {
    const [classes, setClasses] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [showArchived, setShowArchived] = useState(false);
    const [copiedId, setCopiedId] = useState(null);

    const reload = () => {
        const q = showArchived ? '?include_archived=true' : '';
        api.get('/api/teacher/classrooms' + q).then((r) => setClasses(r.data)).catch(() => setClasses([]));
    };

    useEffect(reload, [showArchived]);

    const toggleArchive = async (c) => {
        try {
            await api.put(`/api/teacher/classrooms/${c.id}`, { archived: !c.archived });
            toast.success(c.archived ? 'Class restored' : 'Class archived');
            reload();
        } catch {
            toast.error('Could not update class');
        }
    };

    const copyCode = (code, id) => {
        navigator.clipboard.writeText(code);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 1500);
    };

    return (
        <div className="space-y-6" data-testid="teacher-classes-page">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-3xl sm:text-4xl font-black tracking-tight">My Classes</h1>
                    <p className="text-sm text-zinc-500 mt-1">Create classes, share the join code, and track progress.</p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowArchived(!showArchived)}
                        className="text-xs font-bold text-zinc-600 hover:text-[#7C3AED] px-3 py-2"
                        data-testid="toggle-archived"
                    >
                        {showArchived ? 'Hide archived' : 'Show archived'}
                    </button>
                    <button
                        onClick={() => setShowCreate(true)}
                        className="inline-flex items-center gap-2 bg-[#7C3AED] text-white font-bold text-sm px-5 py-2.5 rounded-xl hover:bg-[#6D28D9]"
                        data-testid="create-class-btn"
                    >
                        <Plus className="w-4 h-4" /> New class
                    </button>
                </div>
            </div>

            {classes === null ? (
                <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 text-[#7C3AED] animate-spin" /></div>
            ) : classes.length === 0 ? (
                <div className="bg-white rounded-3xl border-2 border-dashed border-zinc-200 p-12 text-center" data-testid="empty-classes">
                    <h3 className="text-lg font-black mb-2">No classes yet</h3>
                    <p className="text-sm text-zinc-500 mb-5">Create your first class — you'll get a unique join code to share with students.</p>
                    <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 bg-[#7C3AED] text-white font-bold text-sm px-5 py-2.5 rounded-xl">
                        <Plus className="w-4 h-4" /> Create a class
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {classes.map((c) => (
                        <div key={c.id} className={`bg-white rounded-2xl border ${c.archived ? 'border-zinc-200 opacity-70' : 'border-zinc-200'} p-5 hover:border-[#7C3AED]/40 transition-colors`} data-testid={`class-card-${c.id}`}>
                            <div className="flex items-start justify-between mb-3">
                                <div className="min-w-0">
                                    <div className="text-[10px] uppercase tracking-wider font-bold text-[#7C3AED]">{c.subject}{c.level ? ` · ${c.level}` : ''}</div>
                                    <Link to={`/teacher/classes/${c.id}`} className="font-black text-base hover:text-[#7C3AED] transition-colors line-clamp-2 block" data-testid={`class-link-${c.id}`}>
                                        {c.name}
                                    </Link>
                                    <div className="text-xs text-zinc-500 mt-0.5">{c.year_group || '—'} · {c.academic_year || '—'}</div>
                                </div>
                                <span className={`text-xs font-black px-2 py-1 rounded-md flex-shrink-0 ${pctColor(c.avg_pct || 0)}`}>{Math.round(c.avg_pct || 0)}%</span>
                            </div>

                            <button onClick={() => copyCode(c.class_code, c.id)} className="w-full px-3 py-2 rounded-lg bg-[#F8F7FC] border border-zinc-200 text-left text-xs font-bold flex items-center justify-between hover:bg-[#EDE4FF] transition-colors" data-testid={`copy-code-${c.id}`}>
                                <span><span className="text-zinc-500 mr-2">Class code</span><span className="text-[#7C3AED] tracking-wide">{c.class_code}</span></span>
                                {copiedId === c.id ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5 text-zinc-400" />}
                            </button>

                            <div className="flex items-center justify-between mt-4 text-xs text-zinc-600">
                                <span className="inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {c.student_count} students</span>
                                <span>{c.submission_count} submissions</span>
                            </div>

                            <div className="flex items-center gap-2 mt-4">
                                <Link to={`/teacher/classes/${c.id}`} className="flex-1 text-center bg-zinc-950 text-white text-xs font-bold py-2 rounded-lg hover:bg-zinc-800" data-testid={`open-class-${c.id}`}>Open</Link>
                                <button onClick={() => toggleArchive(c)} className="px-3 py-2 rounded-lg border border-zinc-200 text-xs font-bold hover:bg-zinc-100" title={c.archived ? 'Restore' : 'Archive'} data-testid={`archive-${c.id}`}>
                                    {c.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showCreate && <CreateClassModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); reload(); }} />}
        </div>
    );
}

function CreateClassModal({ onClose, onCreated }) {
    const { subjects } = useSubjects();
    const [form, setForm] = useState({ name: '', subject: 'Economics', level: 'IAL', unit: '', year_group: 'Year 13', academic_year: '2025-26' });
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) return toast.error('Class name is required');
        setSaving(true);
        try {
            await api.post('/api/teacher/classrooms', form);
            toast.success('Class created');
            onCreated();
        } catch {
            toast.error('Could not create class');
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="create-class-modal">
            <div className="bg-white rounded-3xl border-2 border-zinc-950 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-black">Create a class</h3>
                    <button onClick={onClose} className="p-1 hover:bg-zinc-100 rounded-lg"><X className="w-4 h-4" /></button>
                </div>
                <form onSubmit={submit} className="space-y-4">
                    <Field label="Class name">
                        <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Y13 Economics A" className="w-full px-3 py-2.5 rounded-lg border border-zinc-200 focus:border-[#7C3AED] outline-none text-sm" data-testid="form-class-name" autoFocus />
                    </Field>
                    <Field label="Subject">
                        <select value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-zinc-200 outline-none text-sm" data-testid="form-class-subject">
                            {subjects.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                        </select>
                    </Field>
                    <Field label="Level">
                        <div className="inline-flex bg-zinc-100 p-1 rounded-full" data-testid="form-class-level">
                            {LEVELS.map((l) => (
                                <button
                                    key={l.id}
                                    type="button"
                                    onClick={() => setForm({ ...form, level: l.id })}
                                    className={`px-4 py-1.5 text-xs font-bold rounded-full transition-all ${
                                        form.level === l.id ? 'bg-white text-[#7C3AED] shadow-sm' : 'text-zinc-500'
                                    }`}
                                    data-testid={`form-class-level-${l.id}`}
                                >
                                    {l.name}
                                </button>
                            ))}
                        </div>
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Year group">
                            <input type="text" value={form.year_group} onChange={(e) => setForm({ ...form, year_group: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-zinc-200 outline-none text-sm" />
                        </Field>
                        <Field label="Academic year">
                            <input type="text" value={form.academic_year} onChange={(e) => setForm({ ...form, academic_year: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-zinc-200 outline-none text-sm" />
                        </Field>
                    </div>
                    <Field label="Unit (optional)">
                        <input type="text" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="Theme 1 / Unit 2…" className="w-full px-3 py-2.5 rounded-lg border border-zinc-200 outline-none text-sm" />
                    </Field>
                    <button type="submit" disabled={saving} className="w-full bg-[#7C3AED] text-white font-bold text-sm py-3 rounded-xl hover:bg-[#6D28D9] disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="form-class-submit">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        {saving ? 'Creating…' : 'Create class'}
                    </button>
                </form>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-xs font-bold text-zinc-600 mb-1.5 block">{label}</span>
            {children}
        </label>
    );
}
